'use client'
/* eslint-disable react-hooks/rules-of-hooks */

import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { MessageCircle, Send, X, ArrowLeft, ChevronRight, ChevronLeft, Plus, Volume2, Edit, Copy, Check, XCircle, ChevronDown, Mic, Square, Trash2, RotateCw } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { useChatStore } from '@/stores/chatStore'
import { createClient } from '@supabase/supabase-js'
import { useTabVisibility } from '@/hooks/useTabVisibility'
import { Message } from '@/types/chat'
import {
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger,
  SheetFooter,
  SheetClose,
  SheetDescription
} from '@/components/ui/sheet'
import { cn } from "@/lib/utils"
import dynamic from 'next/dynamic'

// Initialize Supabase client
const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL || '',
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''
);

// Session type definition
interface ChatSession {
  id: string
  created_at: string
  title: string
  user_id: string
}

// Format matches n8n Chat Trigger node expectations
interface ChatRequest {
  sessionId: string;
  userId: string;
  messageText: string;
  action: 'sendMessage' | 'loadPreviousSession' | 'sendAudio';
  chatHistory?: Message[];
}

const WEBHOOK_URL = 'https://n8n.automize.site/webhook/4ff8e50b-8729-4c0b-b4d5-a38bb228ffe0/chat'

// Function to format chat message content with proper HTML
const formatMessageContent = (content: string): string => {
  if (!content) return '';
  
  console.log('[formatMessageContent] Original content received:', JSON.stringify(content));
  
  // Check if content contains JSON markdown, and if so, extract just the user-facing message
  if (content.trim().startsWith('```json')) {
    try {
      // Extract just the JSON part
      const jsonString = content
        .replace(/^```json/, '')  // Remove leading ```json
        .replace(/```$/, '')      // Remove trailing ```
        .trim();                 // Trim whitespace
      
      // Try to parse it
      const jsonData = JSON.parse(jsonString);
      
      // If it has a content field, use that instead of the raw markdown
      if (jsonData && typeof jsonData === 'object' && typeof jsonData.content === 'string') {
        console.log('[formatMessageContent] Extracted content from JSON:', jsonData.content);
        content = jsonData.content;
      }
    } catch (error) {
      console.warn('[formatMessageContent] Failed to parse JSON in markdown, using original content:', error);
      // Keep the original content if parsing fails
    }
  }
  
  // Continue with normal prefix stripping and formatting
  const displayContent = stripKnownPrefixes(content);
  console.log('[formatMessageContent] Content after stripping prefixes:', JSON.stringify(displayContent));
  
  // Apply formatting ONLY to the displayContent
  let formattedContent = displayContent;
  
  // Parse markdown-style elements
  
  // Format headers
  formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Format links
  const linkRegex = /\[(.*?)\]\((https?:\/\/[^\s]+)\)/g;
  formattedContent = formattedContent.replace(linkRegex, '<a href="$2" target="_blank" class="text-[#8A6FE8] hover:underline">$1</a>');
  
  // Format raw URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  formattedContent = formattedContent.replace(urlRegex, (url) => {
    // Only replace URLs that aren't already in an HTML tag
    if (url.match(/<a.*?<\/a>/)) return url;
    return `<a href="${url}" target="_blank" class="text-[#8A6FE8] hover:underline">${url}</a>`;
  });
  
  // Convert asterisk bullet lists
  let bulletListBlocks = formattedContent.split(/\n\n+/);
  bulletListBlocks = bulletListBlocks.map(block => {
    // Check if this block looks like a bullet list (lines starting with * or -)
    if (block.match(/^(\s*[\*\-]\s+.+\n?)+$/m)) {
      const items = block.split('\n')
        .filter(line => line.trim().match(/^[\*\-]\s+/))
        .map(line => line.replace(/^(\s*[\*\-]\s+)(.+)$/, '<li>$2</li>'))
        .join('');
      
      return `<ul class="pl-5 list-disc space-y-1">${items}</ul>`;
    }
    return block;
  });
  formattedContent = bulletListBlocks.join('\n\n');
  
  // Handle paragraphs - replace double newlines with paragraph breaks
  formattedContent = formattedContent.replace(/\n\n+/g, '</p><p>');
  
  // Handle single line breaks (outside lists)
  formattedContent = formattedContent.replace(/\n/g, '<br>');
  
  // Wrap with paragraph tags if not already
  if (!formattedContent.startsWith('<p>') && !formattedContent.startsWith('<ul')) {
    formattedContent = `<p>${formattedContent}</p>`;
  }
  
  return formattedContent;
};

// Helper function to remove known prefixes like "Context: ...\n\n" and "User Email: ...\n\n" for display
const stripKnownPrefixes = (content: string): string => {
  console.log('[stripKnownPrefixes] Input content:', JSON.stringify(content));
  let processedContent = content;

  // Remove Context prefix first
  if (processedContent.startsWith('Context:')) {
    const separatorIndex = processedContent.indexOf('\n\n');
    if (separatorIndex !== -1) {
      processedContent = processedContent.substring(separatorIndex + 2).trim(); 
      console.log('[stripKnownPrefixes] After stripping Context::', JSON.stringify(processedContent));
    } else {
      console.log('[stripKnownPrefixes] Context: prefix found, but no separator \'\\n\\n\'.');
    }
  }

  // Remove User Email prefix from the potentially already stripped content
  if (processedContent.startsWith('User Email:')) {
    const separatorIndex = processedContent.indexOf('\n\n');
    if (separatorIndex !== -1) {
      processedContent = processedContent.substring(separatorIndex + 2).trim();
      console.log('[stripKnownPrefixes] After stripping User Email::', JSON.stringify(processedContent));
    } else {
       console.log('[stripKnownPrefixes] User Email: prefix found, but no separator \'\\n\\n\'.');
    }
  }

  if (processedContent === content) {
      console.log('[stripKnownPrefixes] No known prefixes found or stripped. Returning original.');
  }
  
  return processedContent;
};

function ChatButton() {
  const pathname = usePathname()
  const router = useRouter()
  
  // --- Zustand Store Integration ---
  const { 
    isChatOpen, 
    openChat, 
    closeChat, 
    toggleChat,
    messages: allMessages,
    sessionId,
    persistentUserId,
    isInitialized,
    initializeChatState,
    addMessage,
    setMessages,
    setSessionId,
    clearChat
  } = useChatStore();
  // --- End Zustand Store Integration ---

  // State for sidebar visibility - default collapsed (Keep local UI state)
  const [sidebarVisible, setSidebarVisible] = useState(false);
  
  // State for chat sessions list (Keep local UI state)
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [visibleSessionCount, setVisibleSessionCount] = useState(10); 

  // Keep local UI/temporary state
  const [displayCount, setDisplayCount] = useState(10)
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false) // For async operations like sending messages
  const chatMessagesRef = useRef<HTMLDivElement>(null)
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const prevMessagesCountRef = useRef<number>(0);
  
  // Ref to store URLs created for messages that need cleanup (Keep local ref)
  const messageAudioUrlsToRevokeRef = useRef<Set<string>>(new Set());

  // State for editing messages (Keep local UI state)
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');

  // State for text-to-speech (Keep local UI state)
  const [speakingMessageIndex, setSpeakingMessageIndex] = useState<number | null>(null);

  // State for MediaRecorder Audio Recording (Keep local UI state)
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioInputUrl, setAudioInputUrl] = useState<string | null>(null); 
  const [mediaRecorderAvailable, setMediaRecorderAvailable] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null); 

  // Get auth context (including the user object)
  const { user, getAllowedBrandIds, loading: authLoading } = useAuth();

  // State for forcing re-render
  const [renderTrigger, setRenderTrigger] = useState(0);

  // Chat state monitoring
  const [chatStuckState, setChatStuckState] = useState<'normal' | 'stuck'>('normal');

  // Use the new intelligent tab visibility hook - MODIFIED to always reinitialize
  const { isTabVisible } = useTabVisibility({
    onTabReturn: (timeHidden) => {
      console.log(`[Chat] Tab returned after ${timeHidden}ms`);
      
      // Always reinitialize state regardless of time hidden
      console.log('[Chat] Reinitializing chat state after tab return...');
      initializeChatState();
      
      // Reload sessions list
      loadChatSessions();
      
      // Check if chat state appears inconsistent
      const currentState = useChatStore.getState();
      if (currentState.isChatOpen) {
        try {
          const storedMessages = localStorage.getItem('chat_history');
          if (storedMessages) {
            const parsedMessages = JSON.parse(storedMessages);
            if (parsedMessages.length !== currentState.messages.length) {
              console.log('[Chat] Chat state inconsistency detected after reload');
              // Force another initialization after a short delay
              setTimeout(() => {
                initializeChatState();
                setRenderTrigger(prev => prev + 1);
              }, 100);
            }
          }
        } catch (error) {
          console.error('[Chat] Error checking chat state:', error);
        }
      }
    },
    onTabHidden: () => {
      console.log('[Chat] Tab hidden - ensuring state is saved');
      // Force save current state to localStorage when tab is hidden
      const currentState = useChatStore.getState();
      if (currentState.messages && currentState.messages.length > 0) {
        try {
          localStorage.setItem('chat_history', JSON.stringify(currentState.messages));
          console.log('[Chat] Saved messages to localStorage on tab hide');
        } catch (error) {
          console.error('[Chat] Error saving state on tab hide:', error);
        }
      }
    },
    minHiddenTimeForAction: 100, // React quickly to tab switches
    debounceMs: 200 // Shorter debounce for faster response
  });

  // Don't render the chat button on auth pages
  if (pathname?.startsWith('/auth/')) {
    return null
  }

  // --- Helper Function for Robust Response Parsing ---
  const parseContentData = (responseData: any): { content: string | null, actionUrl: string | null } => {
      console.log("[parseContentData] Received responseData:", JSON.stringify(responseData)); 
      
      let parsedContent: string | null = null;
      let parsedActionUrl: string | null = null;

      // Check if responseData.output is a string containing markdown JSON
      if (responseData && typeof responseData.output === 'string') { 
          console.log("[parseContentData] responseData.output IS a string:", JSON.stringify(responseData.output));
          
          if (responseData.output.trim().startsWith('```json')) {
              console.log("[parseContentData] Detected markdown JSON in output field."); // Changed log message
              let jsonString = '';
              try {
                  jsonString = responseData.output // <-- Use .output
                      .replace(/^```json/, '') 
                      .replace(/```$/, '')     
                      .trim();                 
                  
                  console.log("[parseContentData] Raw JSON string after stripping markdown:", JSON.stringify(jsonString)); 
                  
                  let unescapedString = jsonString;
                  if (jsonString.includes('\\\\') || jsonString.includes('\\"')) {
                      console.log("[parseContentData] Detected escaped JSON string, attempting to unescape");
                      try {
                          unescapedString = JSON.parse(`"${jsonString.replace(/^"|"$/g, '').replace(/"/g, '\\"')}"`);
                      } catch (unescapeError) {
                          console.error("[parseContentData] Failed to unescape using JSON.parse, trying alternate method");
                          unescapedString = jsonString
                              .replace(/\\\\/g, '\\') 
                              .replace(/\\"/g, '"')    
                              .replace(/\\n/g, '\n'); 
                      }
                      console.log("[parseContentData] Unescaped string:", JSON.stringify(unescapedString));
                  }
                  
                  const innerData = JSON.parse(unescapedString);
                  console.log("[parseContentData] Successfully parsed inner JSON:", innerData);
                  
                  if (innerData && typeof innerData === 'object') {
                      console.log("[parseContentData] Inspecting innerData:", JSON.stringify(innerData, null, 2)); 
                      if (typeof innerData.content === 'string') {
                          parsedContent = innerData.content;
                          console.log("[parseContentData] innerData.content found:", parsedContent);
                      }
                      console.log("[parseContentData] Checking innerData.action exists:", !!innerData.action);
                      if (innerData.action && typeof innerData.action === 'object') {
                        console.log("[parseContentData] innerData.action is object. Checking type:", innerData.action.type);
                        if (innerData.action.type === 'navigate') {
                          console.log("[parseContentData] innerData.action.type is navigate. Checking url:", innerData.action.url);
                          if (typeof innerData.action.url === 'string') {
                            parsedActionUrl = innerData.action.url;
                            console.log("[parseContentData] innerData.action.url found and assigned:", parsedActionUrl);
                          } else {
                            console.warn("[parseContentData] innerData.action.url is not a string:", innerData.action.url);
                          }
                        } else {
                           console.warn("[parseContentData] innerData.action.type is not 'navigate':", innerData.action.type);
                        }
                      } else {
                          console.warn("[parseContentData] innerData.action not found or not an object.");
                      }
                  }
              } catch (parseError) {
                  console.error("[parseContentData] Failed to parse inner JSON.");
                  console.error("[parseContentData] Original output content:", responseData.output); // <-- Use .output
                  console.error("[parseContentData] String attempted to parse:", JSON.stringify(jsonString));
                  console.error("[parseContentData] Parse Error:", parseError);
                  parsedContent = responseData.output; // <-- Fallback to .output
                  if (responseData.action && typeof responseData.action === 'object' && responseData.action.type === 'navigate' && typeof responseData.action.url === 'string') {
                      parsedActionUrl = responseData.action.url;
                  }
              }
          } else {
              // If responseData.output is a string but NOT markdown JSON, treat it as content
              console.log("[parseContentData] responseData.output is a plain string, using as content.");
              parsedContent = responseData.output;
              // Still check for action directly on responseData (less likely structure now)
              if (responseData.action && typeof responseData.action === 'object' && responseData.action.type === 'navigate' && typeof responseData.action.url === 'string') {
                 parsedActionUrl = responseData.action.url;
              }
          }
      } else {
         // --- Fallback / Original logic if responseData.output isn't the source --- 
         console.log("[parseContentData] responseData.output not found or not a string. Checking other fields.");
         if (responseData && typeof responseData === 'object') {
             if (typeof responseData.content === 'string') { 
                 parsedContent = responseData.content;
             } else if (responseData.type === 'ai' && typeof responseData.content === 'string') {
                 parsedContent = responseData.content;
             } else if (responseData.messageText) {
                 parsedContent = responseData.messageText;
             } else if (responseData.message) {
                 parsedContent = responseData.message;
             } else if (responseData.text) {
                 parsedContent = responseData.text;
             } else if (responseData.response) {
                 parsedContent = responseData.response;
             }
             // Check for action directly on responseData 
             if (responseData.action && typeof responseData.action === 'object' && responseData.action.type === 'navigate' && typeof responseData.action.url === 'string') {
                 parsedActionUrl = responseData.action.url;
             }
         } else if (typeof responseData === 'string') {
             parsedContent = responseData;
         } else {
             parsedContent = 'Sorry, I couldn\'t process your request. (Invalid response type)';
         }
      }
          
      // Set default message if only action exists
      if (!parsedContent && parsedActionUrl) {
          parsedContent = "Okay, navigating now..."; 
      } else if (!parsedContent && !parsedActionUrl) {
          parsedContent = 'Sorry, I couldn\'t process your request. (Unknown object format)';
      }
      
      return { content: parsedContent, actionUrl: parsedActionUrl };
  };
  // --- End Helper Function ---

  // --- Event Listener for External Messages ---
  useEffect(() => {
    const handleOpenChatMessage = (event: Event) => {
      // Type assertion for CustomEvent
      const customEvent = event as CustomEvent<{ message: string }>;
      
      if (customEvent.detail && typeof customEvent.detail.message === 'string') {
        const messageText = customEvent.detail.message;
        console.log('[handleOpenChatMessage] Received message:', messageText);
        
        // 1. Open the chat panel
        openChat(); 
        
        // 2. Send the message (sendChatMessage handles adding user message + calling webhook)
        // Use a small timeout to ensure the chat is visually open before sending
        setTimeout(() => {
            console.log('[handleOpenChatMessage] Sending message via sendChatMessage...');
            sendChatMessage(messageText);
        }, 100); // 100ms delay might be enough
      } else {
        console.warn('[handleOpenChatMessage] Received event without valid message detail:', event);
      }
    };

    console.log('[ChatButton Effect] Adding listener for open-chat-with-message');
    window.addEventListener('open-chat-with-message', handleOpenChatMessage);

    // Cleanup listener on component unmount
    return () => {
      console.log('[ChatButton Effect] Removing listener for open-chat-with-message');
      window.removeEventListener('open-chat-with-message', handleOpenChatMessage);
    };
    // Add dependencies: openChat, sendChatMessage (ensure stable references or include them)
    // Note: If sendChatMessage isn't stable due to dependencies, consider wrapping it in useCallback.
    // For now, let's assume they are stable enough or handled by ESLint hooks rule.
  }, [openChat]); // Re-run if openChat changes (should be stable)
  // --- End Event Listener ---

  // Function to scroll chat to the bottom (Keep as is)
  const scrollToBottom = (force: boolean = false) => {
    // Only scroll if not restoring scroll, OR if force is true
    if (chatMessagesRef.current && (!isRestoringScroll || force)) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
            console.log(`scrollToBottom called: scrollHeight=${chatMessagesRef.current.scrollHeight}, isRestoringScroll=${isRestoringScroll}, forced=${force}`);
          }
        }, 150); // Ensure delay is 150ms
      });
    }
  };

  // Function to load chat sessions (Keep as is, uses Supabase)
  const loadChatSessions = async () => {
    console.log('[loadChatSessions] Starting... Reading from n8n_chat_histories.');
    setIsLoadingSessions(true);
    setSessions([]); // Clear existing sessions

    try {
      console.log('[loadChatSessions] Fetching all messages to determine session titles and dates...');
      // Fetch all relevant message data, ordered by ID to easily find the first message
      const { data: allMessagesData, error } = await supabase
        .from('n8n_chat_histories')
        .select('id, session_id, message, created_at') // Include created_at
        .order('id', { ascending: true }); 

      if (error) {
        console.error('[loadChatSessions] Supabase error fetching messages:', error);
        throw error;
      }

      console.log('[loadChatSessions] All messages fetched successfully:', allMessagesData);

      if (allMessagesData && allMessagesData.length > 0) {
        // Process messages to group by session, find title, and first timestamp
        const sessionsMap = new Map<string, { title: string, created_at: string, firstMessageFound: boolean, titleFound: boolean }>();

        for (const entry of allMessagesData) {
          if (!entry.session_id || !entry.message || !entry.created_at) continue; // Skip invalid entries

          const sessionId = entry.session_id;
          const messageData = entry.message as any; // Use 'any' for easier access
          const createdAt = entry.created_at;

          // Initialize session in map if it doesn't exist
          if (!sessionsMap.has(sessionId)) {
            sessionsMap.set(sessionId, {
              title: sessionId, // Default title is the ID
              created_at: createdAt, // Set initial created_at from the first message encountered
              firstMessageFound: true, // Mark that we've found the first message (lowest ID)
              titleFound: false // Mark that we haven't found the human title yet
            });
          } 
          // No need to update created_at further, as we ordered by ID ASC

          const currentSession = sessionsMap.get(sessionId)!;

          // Check if this is a human message and if we haven't found a title yet
          if (!currentSession.titleFound &&
              typeof messageData === 'object' && 
              messageData !== null && 
              messageData.type === 'human' && 
              typeof messageData.content === 'string' &&
              messageData.content.trim() !== '') 
          {
            const actualMessageContent = stripKnownPrefixes(messageData.content);
            const firstQuery = actualMessageContent.slice(0, 50) + (actualMessageContent.length > 50 ? '...' : '');
            
            if (firstQuery.trim()) {
               sessionsMap.set(sessionId, { 
                ...currentSession,
                title: firstQuery, 
                titleFound: true // Mark title as found
              });
            }
          }
        }

        // Convert map to ChatSession array
        let formattedSessions: ChatSession[] = Array.from(sessionsMap.entries()).map(([id, data]) => ({
          id: id,
          title: data.title,
          created_at: data.created_at,
          user_id: user?.id || 'unknown', 
        }));
        
        // Sort sessions by created_at date descending (most recent first)
        formattedSessions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        console.log(`[loadChatSessions] Processed ${formattedSessions.length} sessions. Setting state.`);
        setSessions(formattedSessions);

      } else {
        console.log('[loadChatSessions] No messages found in n8n_chat_histories.');
        setSessions([]);
      }

    } catch (error) {
      console.error('[loadChatSessions] Error during session loading from n8n_chat_histories: ', error);
      setSessions([]); // Clear sessions on error
    } finally {
      setIsLoadingSessions(false);
      console.log('[loadChatSessions] Finished reading from n8n_chat_histories.');
    }
  };

  // Function to load a specific chat session (Uses Zustand actions)
  const loadSessionById = async (targetSessionId: string) => {
    // Prevent loading the same session again if it's already selected
    if (targetSessionId === sessionId && allMessages.length > 1) { // Check > 1 to allow reloading initial state
      console.log(`[loadSessionById] Session ${targetSessionId} is already loaded. Skipping.`);
      return;
    }
    
    console.log(`[loadSessionById] Attempting to load session: ${targetSessionId} from n8n_chat_histories`);
    try {
      setIsLoading(true); // Use local loading state for feedback (e.g., spinner/disabled UI)
      
      // Keep fetching logic
      console.log(`[loadSessionById] Fetching messages for session: ${targetSessionId}`);
      const { data: historyEntries, error } = await supabase
        .from('n8n_chat_histories')
        .select('id, message, created_at') // Include created_at
        .eq('session_id', targetSessionId)
        .order('id', { ascending: true });

      if (error) {
        console.error(`[loadSessionById] Supabase error fetching messages for session ${targetSessionId}:`, error);
        // Display error message without clearing existing messages immediately
        // Option: Add error as a new message? Or replace?
        // Let's add it as a new message temporarily.
        addMessage({ 
            role: 'assistant', 
            content: `Error loading session: ${error.message}` 
        });
        // We still need to set the session ID so the sidebar highlights correctly
        setSessionId(targetSessionId); 
        // We didn't successfully load, so maybe don't scroll?
        // throw error; // Or re-throw if needed elsewhere
        return; // Stop processing on fetch error
      }
      
      console.log(`[loadSessionById] Raw history entries fetched for session ${targetSessionId}:`, historyEntries);

      if (historyEntries && historyEntries.length > 0) {
        console.log(`[loadSessionById] Found ${historyEntries.length} message entries for session ${targetSessionId}`);
        const formattedMessages: Message[] = historyEntries
          .map(entry => {
            // ... validation logic ...
            if (/* valid */ entry && typeof entry.message === 'object' && entry.message !== null && typeof entry.message.type === 'string' && typeof entry.message.content === 'string') {
              const role = entry.message.type === 'human' ? 'user' : 'assistant';
              const rawContent = entry.message.content;
              // console.log(`[loadSessionById] Extracted raw content for role ${role}:`, JSON.stringify(rawContent));
              return { 
                role: role, 
                content: rawContent, 
                timestamp: entry.created_at // Add timestamp from fetched data
              } as Message;
            } else {
              console.warn('[loadSessionById] Invalid or unexpected message format found in entry:', entry);
              return null; 
            }
          })
          .filter((msg): msg is Message => msg !== null); 

        console.log(`[loadSessionById] Formatted messages for session ${targetSessionId} (before setting state):`, formattedMessages);

        // Set the actual messages and session ID using Zustand actions
        setMessages(formattedMessages); 
        setSessionId(targetSessionId); 

        // Scroll to bottom after new messages are loaded and rendered
        // Add a slightly longer delay to ensure rendering completes
        setTimeout(() => scrollToBottom(true), 150); 
        
      } else {
         console.log(`[loadSessionById] No message entries found for session ${targetSessionId}`);
         // Set an empty state message or keep previous messages?
         // Let's set a specific message for this case.
         setMessages([
           {
             role: 'assistant',
             content: `No messages found for session ${targetSessionId.substring(0, 8)}.`,
           },
         ]);
         // Still set the session ID
         setSessionId(targetSessionId);
      }
    } catch (error) {
      // This catch block might be redundant if the fetch error is handled above
      console.error('[loadSessionById] Unexpected error during session loading:', error);
       addMessage({ 
          role: 'assistant',
          content: 'An unexpected error occurred while loading session history.',
        });
       // Optionally set session ID even on unexpected error?
       // setSessionId(targetSessionId);
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  // Function to start a new chat (Uses Zustand action which handles localStorage)
  const handleNewChat = () => {
    clearChat(); 
    setInputValue(''); 
    setIsLoading(false); 
    loadChatSessions(); 
  };

  // Initialize state using Zustand action
  useEffect(() => {
    console.log('[ChatButton] Component mounted. Triggering initializeChatState...');
    // Ensure this runs only once by checking isInitialized inside the action
    initializeChatState(); 
  }, [initializeChatState]);

  // Add visibility change listener to reinitialize on tab return
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[ChatButton] Tab became visible, reinitializing chat state...');
        // Force reinitialization when tab becomes visible
        initializeChatState();
        // Also reload sessions list
        loadChatSessions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [initializeChatState]);

  // Load sidebar sessions when chat is opened OR when auth state loads OR when Zustand state is initialized
  useEffect(() => {
    console.log(`[Session Load useEffect] Running. isInitialized=${isInitialized}, authLoading=${authLoading}`);
    if (isInitialized && !authLoading) { 
       console.log('[Session Load useEffect] Conditions met. Reloading sidebar sessions.');
       loadChatSessions(); // This loads the list, NOT a specific session
    } else {
      console.log('[Session Load useEffect] Conditions NOT met for loading sidebar sessions:', { isInitialized, authLoading });
    }
  }, [isChatOpen, isInitialized, authLoading]); 

  // Scroll on new message effect - depends on displayedMessages derived below
  useEffect(() => {
    // Derive displayedMessages *inside* the effect for comparison
    const currentDisplayedMessages = allMessages.slice(Math.max(0, allMessages.length - displayCount));
    
    // Check if the number of messages has actually increased
    if (currentDisplayedMessages.length > prevMessagesCountRef.current && !isRestoringScroll) {
      console.log(`[Scroll Effect] Message count increased (${prevMessagesCountRef.current} -> ${currentDisplayedMessages.length}). Scrolling.`);
      scrollToBottom(); 
    } else {
       console.log(`[Scroll Effect] Message count did not increase or restoring scroll. No scroll triggered. Prev: ${prevMessagesCountRef.current}, Curr: ${currentDisplayedMessages.length}, Restoring: ${isRestoringScroll}`);
    }
    // Update the ref *after* the check
    prevMessagesCountRef.current = currentDisplayedMessages.length;
    // Depend on allMessages and displayCount directly now, plus isRestoringScroll
  }, [allMessages, displayCount, isRestoringScroll]); 

  // Function to load more previous messages (Keep as is, updates local displayCount)
  const loadMoreMessages = () => {
    if (chatMessagesRef.current) {
      // Set the flag *before* changing state that triggers re-render
      setIsRestoringScroll(true);
      
      // Remember the current scroll position
      const currentScrollHeight = chatMessagesRef.current.scrollHeight;
      const currentScrollTop = chatMessagesRef.current.scrollTop;
      
      // Increase the display count
      setDisplayCount(prev => prev + 10);
      
      // After the new messages are rendered, restore the relative scroll position
      // Use requestAnimationFrame to wait for the DOM update after setDisplayCount
      requestAnimationFrame(() => {
        if (chatMessagesRef.current) {
          // Calculate how much new content was added
          const newScrollHeight = chatMessagesRef.current.scrollHeight;
          const addedHeight = newScrollHeight - currentScrollHeight;
          
          // Keep the same relative scroll position
          chatMessagesRef.current.scrollTop = currentScrollTop + addedHeight;
          
          // Reset the loading flag *after* scroll adjustment
          // Use another timeout to ensure it happens after the scroll completes
          setTimeout(() => {
            setIsRestoringScroll(false);
          }, 50); // Small delay might be needed
        } else {
          // If ref is somehow null, reset the flag anyway
          setIsRestoringScroll(false);
        }
      });
    }
  };

  // --- Send Chat Message Logic (Handles Text Only) ---
  const sendChatMessage = async (messageText: string) => {
    const currentSessionId = useChatStore.getState().sessionId;
    const currentUserId = useChatStore.getState().persistentUserId;

    if (!messageText || !messageText.trim() || isLoading || !currentSessionId || !currentUserId) {
      console.log('Not sending message: Input validation failed', {
        hasInput: !!messageText.trim(),
        isLoading,
        hasSessionId: !!currentSessionId,
        hasUserId: !!currentUserId
      });
      return;
    }
    
    const userMessage: Message = {
      role: 'user',
      content: messageText, 
      timestamp: new Date().toISOString() // Add current timestamp
    };
    
    // Add user message via Zustand action
    addMessage(userMessage); 

    setInputValue(''); 
    setIsLoading(true);
    
    // --- Add Brand Access Context for backend --- 
    let brandContext = '';
    let allowedBrandAccess: string[] | 'all' | 'none' = 'none'; 
    if (authLoading) {
      brandContext = 'Context: User brand access information is loading.\n\n';
    } else {
      allowedBrandAccess = getAllowedBrandIds(); 
      if (allowedBrandAccess === 'all') {
        brandContext = 'Context: User has access to all brands.\n\n';
      } else if (allowedBrandAccess === 'none') {
        brandContext = 'Context: User has access to no brands.\n\n';
      } else if (Array.isArray(allowedBrandAccess) && allowedBrandAccess.length > 0) {
        brandContext = `Context: User has access to brand IDs: ${allowedBrandAccess.join(', ')}.\n\n`;
      } else {
        brandContext = 'Context: User has restricted brand access (no specific brands assigned).\n\n';
        allowedBrandAccess = 'none'; 
      }
    }
    if (user?.email) {
      brandContext += `User Email: ${user.email}\n\n`;
    }
    const finalMessageText = brandContext + messageText;
    // --- End Brand Access Context ---

    try {
      // Get latest messages *before* sending request
      const messagesToSend = useChatStore.getState().messages; 
      
      const request: ChatRequest = {
        userId: currentUserId, // Use ID from store
        sessionId: currentSessionId, // Use ID from store
        messageText: finalMessageText, 
        action: 'sendMessage',
        chatHistory: messagesToSend.map(m => ({...m})) // Send current history from store
      };
      
      if (!request.messageText || request.messageText.trim() === brandContext.trim() || request.messageText.trim() === '') {
        console.log('Aborting webhook call: finalMessageText is effectively empty after context.');
        setIsLoading(false);
        setInputValue(messageText); // Restore input
        return;
      }
      
      console.log('Sending chat request (text):', request);
      
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get response from webhook: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Received webhook response (text):', data);
      
      // --- Process Response ---
      // Determine the actual data object (handle potential array response)
      let responseDataObject: any = null;
      if (Array.isArray(data) && data.length > 0) {
        console.log("[sendChatMessage] Webhook response is an array, using first element.");
        responseDataObject = data[0];
      } else if (typeof data === 'object' && data !== null) {
         console.log("[sendChatMessage] Webhook response is an object.");
        responseDataObject = data;
      } else {
        // Handle unexpected raw string or other types directly if necessary
         console.log("[sendChatMessage] Webhook response is not an array or object, treating as raw data.");
        responseDataObject = data; // Let parseContentData handle raw strings etc.
      }
      
      // Check if the response itself is a string that is a markdown JSON block
      let processedData = responseDataObject; // Start with the determined object/data
      if (typeof responseDataObject === 'string' && responseDataObject.trim().startsWith('```json')) {
        try {
          // Extract just the JSON part
          const jsonString = responseDataObject.trim()
            .replace(/^```json/, '')
            .replace(/```$/, '')
            .trim();
            
            console.log('[sendChatMessage] Attempting to parse markdown-wrapped JSON response:', jsonString);
            
            // Try to parse it
            const parsedJson = JSON.parse(jsonString);
            
            // Use the parsed object instead
            processedData = parsedJson;
            console.log('[sendChatMessage] Successfully parsed markdown JSON response:', processedData);
        } catch (error) {
          console.error('[sendChatMessage] Failed to parse markdown-wrapped JSON response:', error);
          // Keep original data if parsing fails
        }
      }
      
      // Use the robust parser for text messages
      const parsedTextResult = parseContentData(processedData);
      let assistantResponse: string | null = parsedTextResult.content;
      let navigationUrl: string | null = parsedTextResult.actionUrl;
      
      if (navigationUrl) {
         console.log(`>>> NAVIGATING NOW to: ${navigationUrl}`);
      }

      // Add assistant message *only* if there's content
      if (assistantResponse) {
        const assistantMessage: Message = {
           role: 'assistant',
           content: assistantResponse,
           timestamp: new Date().toISOString() // Add current timestamp
        };
        addMessage(assistantMessage);
      }

      // Perform navigation *after* adding message (if applicable)
      if (navigationUrl) {
        console.log(`>>> NAVIGATING NOW to: ${navigationUrl}`);
        router.push(navigationUrl);
        closeChat(); // Optionally close chat after initiating navigation
      }

    } catch (error) {
      console.error('Error sending message to webhook:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, there was an error processing your request. Please try again later.',
        timestamp: new Date().toISOString() // Add current timestamp
      };
      // Add error message via Zustand action
      addMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  // --- End Send Chat Message ---

  // Updated handleSendMessage to use Zustand state
  const handleSendMessage = async () => {
    setIsLoading(true); 
    
    const currentAudioBlob = audioBlob; 
    const currentAudioUrl = audioInputUrl; 
    const currentInputText = inputValue; 
    
    const currentSessionId = useChatStore.getState().sessionId;
    const currentUserId = useChatStore.getState().persistentUserId;
    
    console.log('[handleSendMessage] Captured state: currentAudioUrl=', currentAudioUrl, 'currentAudioBlob size=', currentAudioBlob?.size);

    // Clear local UI state immediately
    setAudioBlob(null); 
    setAudioInputUrl(null);
    setInputValue(''); 
    
    if (currentAudioBlob && currentAudioUrl) {
      console.log('Preparing to send audio blob:', currentAudioBlob);
      
      if (!currentSessionId || !currentUserId) {
        console.error('Cannot send audio: Missing sessionId or persistentUserId from store.');
        setIsLoading(false);
        // Maybe add an error message to the chat?
         addMessage({ role: 'assistant', content: 'Error: Could not identify user/session. Cannot send audio.'});
        return;
      }

      const userAudioMessage: Message = {
        role: 'user',
        content: currentInputText, 
        audioUrl: currentAudioUrl, 
        audioType: currentAudioBlob.type,
        timestamp: new Date().toISOString() // Add current timestamp
      };
      console.log('[handleSendMessage] Adding user audio message to store:', userAudioMessage);
      // Add message via Zustand action
      addMessage(userAudioMessage); 

      if (currentAudioUrl) {
        messageAudioUrlsToRevokeRef.current.add(currentAudioUrl);
        console.log('[handleSendMessage] Added URL to revocation set:', currentAudioUrl);
      }

      const formData = new FormData();
      formData.append('voice_file', currentAudioBlob, 'recording.webm'); 
      formData.append('mime_type', currentAudioBlob.type);
      formData.append('file_size', currentAudioBlob.size.toString());
      formData.append('session_id', currentSessionId); // Use ID from store
      formData.append('user_id', currentUserId); // Use ID from store
      if (currentInputText.trim()) {
        formData.append('text_message', currentInputText.trim());
      }
      formData.append('action', 'sendAudio'); 

      try {
        console.log('Sending FormData to webhook...');
        const response = await fetch(WEBHOOK_URL, {
          method: 'POST',
          body: formData, 
        });

        if (!response.ok) {
          throw new Error(`Webhook response not OK: ${response.status} ${response.statusText}`);
        }

        const data = await response.json(); 
        console.log('Received webhook response (audio send):', data);

        // --- Handle n8n response --- 
         // Determine the actual data object (handle potential array response)
        let responseDataObject: any = null;
        if (Array.isArray(data) && data.length > 0) {
          console.log("[handleSendMessage] Webhook response is an array, using first element.");
          responseDataObject = data[0];
        } else if (typeof data === 'object' && data !== null) {
           console.log("[handleSendMessage] Webhook response is an object.");
          responseDataObject = data;
        } else {
           console.log("[handleSendMessage] Webhook response is not an array or object, treating as raw data.");
          responseDataObject = data; 
        }
        
        // Check if the response itself is a string that is a markdown JSON block
        let processedData = responseDataObject; // Start with the determined object/data
        if (typeof responseDataObject === 'string' && responseDataObject.trim().startsWith('```json')) {
          try {
            // Extract just the JSON part
            const jsonString = responseDataObject.trim()
              .replace(/^```json/, '')
              .replace(/```$/, '')
              .trim();
              
              console.log('[handleSendMessage] Attempting to parse markdown-wrapped JSON response:', jsonString);
              
              // Try to parse it
              const parsedJson = JSON.parse(jsonString);
              
              // Use the parsed object instead
              processedData = parsedJson;
              console.log('[handleSendMessage] Successfully parsed markdown JSON response:', processedData);
          } catch (error) {
            console.error('[handleSendMessage] Failed to parse markdown-wrapped JSON response:', error);
            // Keep original data if parsing fails
          }
        }
        
        // Use the same robust parser helper function defined outside
        const parsedAudioResult = parseContentData(processedData); 
        let assistantResponse: string | null = parsedAudioResult.content;
        let navigationUrl: string | null = parsedAudioResult.actionUrl;

        // Adjust default message slightly for audio context if needed
        if (!assistantResponse && navigationUrl) {
           assistantResponse = "Okay, processing audio and navigating...";
        } else if (!assistantResponse && !navigationUrl) {
            // If neither text nor navigation found in object from audio response
           assistantResponse = 'Received unclear response after audio.'; 
        } 
        // ---------------------------
        
        // Add response via Zustand action if content exists
        if (assistantResponse) {
            const assistantMessage: Message = {
                role: 'assistant',
                content: assistantResponse,
                timestamp: new Date().toISOString() // Add current timestamp
            };
            addMessage(assistantMessage);
        }

        // Perform navigation *after* adding message (if applicable)
        if (navigationUrl) {
            console.log(`>>> NAVIGATING NOW (from audio) to: ${navigationUrl}`);
            router.push(navigationUrl);
            closeChat(); // Optionally close chat after initiating navigation
        }

      } catch (error) {
        console.error('Error sending audio FormData:', error);
         const errorMessage: Message = {
           role: 'assistant',
           content: 'Sorry, there was an error sending the audio.',
           timestamp: new Date().toISOString() // Add current timestamp
         };
        // Add error via Zustand action
        addMessage(errorMessage);
      } finally {
        setIsLoading(false); 
      }

    } else if (currentInputText.trim()) {
      // Standard text sending logic (already uses Zustand actions)
      await sendChatMessage(currentInputText);
    } else {
       console.log('Nothing to send.');
       setIsLoading(false); 
    }
  };

  // --- MediaRecorder Setup and Handlers (Keep as is) ---
  useEffect(() => {
    // Check for MediaRecorder API availability
    // Removed redundant check for navigator.mediaDevices.getUserMedia
    if ('MediaRecorder' in window) { 
      console.log('MediaRecorder API is available.');
      setMediaRecorderAvailable(true);
    } else {
      console.warn('MediaRecorder API not available in this browser.');
      setMediaRecorderAvailable(false);
    }
    // No cleanup needed here, setup happens on demand
  }, []);

  const setupRecorderEvents = (recorder: MediaRecorder) => {
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
        console.log('Audio chunk received:', event.data.size);
      }
    };

    recorder.onstop = () => {
      const completeBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' }); 
      // Log Blob size HERE
      console.log('[Recorder onstop] Blob created. Size:', completeBlob.size, 'Type:', completeBlob.type);

      if (completeBlob.size === 0) {
        console.warn('[Recorder onstop] Warning: Created audio Blob has size 0.');
        // Potentially handle this case - maybe show an error?
      }

      const url = URL.createObjectURL(completeBlob);
      setAudioBlob(completeBlob);
      setAudioInputUrl(url); // Set the playable URL for the input area
      console.log('[Recorder onstop] Recording stopped. Object URL created:', url);
      
      // Do NOT track the input URL for revocation here yet
      
      // Stop media tracks to release microphone indicator
      audioStreamRef.current?.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
      setIsRecording(false);
    };
  };

  const startRecording_modified = async () => {
    if (!mediaRecorderAvailable) {
      alert('Audio recording is not supported by your browser.');
      return;
    }
    setAudioBlob(null); 
    setAudioInputUrl(null); // Clear previous URL
    audioChunksRef.current = []; 

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream; 
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      
      setupRecorderEvents(recorder); // Setup events here
      
      recorder.start();
      setIsRecording(true);
      console.log('Recording started...');

    } catch (err) {
      console.error('Error accessing microphone or starting recorder:', err);
      if ((err as Error).name === 'NotAllowedError' || (err as Error).name === 'PermissionDeniedError') {
        alert('Microphone permission denied. Please allow microphone access in your browser settings.');
      } else {
        alert('Could not start recording. Please ensure a microphone is connected and permissions are granted.');
      }
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop(); // This will trigger the onstop handler
      console.log('Stopping recording manually...');
    }
  };
  
  const cancelRecording = () => {
     console.log('Cancelling recording...');
     if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop(); 
     }
     audioStreamRef.current?.getTracks().forEach(track => track.stop());
     audioStreamRef.current = null;
     // Clear state including URL
     setIsRecording(false);
     setAudioBlob(null);
     // Don't revoke URL here on cancel, let cleanup handle it
     // if (audioInputUrl) {
     //     URL.revokeObjectURL(audioInputUrl); // Clean up URL immediately on cancel
     //     console.log('Revoked URL on cancel:', audioInputUrl);
     // }
     setAudioInputUrl(null);
     audioChunksRef.current = [];
  };
  // --- End MediaRecorder ---

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // --- Text-to-Speech Handlers (Keep as is) ---
  const handleReadAloud = (text: string, index: number) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.error('Speech synthesis not supported in this browser.');
      alert("Sorry, your browser doesn't support text-to-speech.");
      return;
    }

    if (speakingMessageIndex === index) {
      window.speechSynthesis.cancel();
      setSpeakingMessageIndex(null);
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(stripKnownPrefixes(text).replace(/<[^>]*>?/gm, '')); 
    
    utterance.onstart = () => {
      setSpeakingMessageIndex(index);
      console.log('Speech started for index:', index);
    };
    
    utterance.onend = () => {
      setSpeakingMessageIndex(null);
      console.log('Speech ended for index:', index);
    };
        
    window.speechSynthesis.speak(utterance);
  };

  // --- Edit Handlers (Update to use Zustand actions for local update) ---
  // Note: Editing messages and syncing back to the server/other sessions is complex.
  // This implementation will ONLY update the local store/localStorage for the edit.
  const handleStartEdit = (indexInDisplayed: number) => {
    // Find the corresponding index in the full `allMessages` from the store
    const actualIndex = allMessages.length - indexInDisplayed;
    
    if (allMessages[actualIndex]?.role === 'user') {
      const rawContent = allMessages[actualIndex].content;
      setEditingContent(stripKnownPrefixes(rawContent));
      setEditingMessageIndex(actualIndex); // Store the index relative to allMessages
    } else {
      console.warn('Cannot edit assistant messages.');
    }
  };

  const handleSaveEdit = (index: number) => { // index here is relative to allMessages
    if (editingMessageIndex === null || editingMessageIndex !== index) return;

    const originalRawContent = allMessages[index].content;
    let newRawContent = editingContent; 

    // ... (keep prefix re-adding logic as is) ...
    if (originalRawContent.startsWith('Context:')) {
      const contextPart = originalRawContent.substring(0, originalRawContent.indexOf('\n\n') + 2);
      if (originalRawContent.substring(contextPart.length).startsWith('User Email:')) {
        const emailPart = originalRawContent.substring(contextPart.length, originalRawContent.indexOf('\n\n', contextPart.length) + 2);
        newRawContent = contextPart + emailPart + editingContent;
      } else {
        newRawContent = contextPart + editingContent;
      }
    } else if (originalRawContent.startsWith('User Email:')) {
       const emailPart = originalRawContent.substring(0, originalRawContent.indexOf('\n\n') + 2);
       newRawContent = emailPart + editingContent;
    }

    // Create a *new* messages array with the updated message
    const updatedMessages = allMessages.map((msg, i) => 
       i === index ? { ...msg, content: newRawContent } : msg
    );

    // Update the messages in the Zustand store
    setMessages(updatedMessages); 
    
    console.warn('Edit saved locally (store handles persistence). No server update.');

    setEditingMessageIndex(null);
    setEditingContent('');
  };

  const handleCancelEdit = () => {
    setEditingMessageIndex(null);
    setEditingContent('');
  };
  // --- End Edit Handlers ---

  // Cleanup Object URLs on unmount (Keep as is)
  useEffect(() => {
    // Store URLs created during the component lifecycle
    const createdUrls = new Set<string>();

    // Modify loadSessionById to track loaded URLs if they exist (assuming URLs aren't persisted)
    // Or, more simply, just handle URLs created in *this* component instance.

    // Get the current set of URLs to revoke at the time the effect runs
    const urlsToRevoke = messageAudioUrlsToRevokeRef.current;

    return () => {
      console.log('[Cleanup] Revoking tracked message audio Object URLs...');
      urlsToRevoke.forEach(url => {
        console.log(`[Cleanup] Revoking message URL: ${url}`);
        URL.revokeObjectURL(url);
      });
      // Also revoke the current input URL if it exists and wasn't added to messages
      if (audioInputUrl && !urlsToRevoke.has(audioInputUrl)) {
        console.log(`[Cleanup] Revoking dangling input URL: ${audioInputUrl}`);
        URL.revokeObjectURL(audioInputUrl);
      }
      // Clear the ref for the next mount? Not strictly necessary but good practice.
      messageAudioUrlsToRevokeRef.current.clear();
    };
  }, []); // Run only on mount and unmount

  // Refresh Handler - MODIFIED FOR DIAGNOSTICS
  const handleRefreshChat = () => {
    console.log("[handleRefreshChat] Refresh button clicked. Performing page reload (diagnostic)...");
    window.location.reload(); 
  }

  // Function to manually refresh chat when stuck
  const handleManualChatReload = () => {
    console.log('[Chat] Manual reload triggered');
    setChatStuckState('normal');
    useChatStore.getState().initializeChatState();
    loadChatSessions();
    setRenderTrigger(prev => prev + 1);
  };

  // Calculate displayedMessages directly before rendering
  const displayedMessages = allMessages.slice(Math.max(0, allMessages.length - displayCount));

  // --- Log state received by component render ---
  console.log(`[Render] Component rendering. Zustand hook state: sessionId=${sessionId}, messages=${allMessages.length}, renderTrigger=${renderTrigger}`);

  return (
    <Sheet
      open={isChatOpen} // Use state from store
      onOpenChange={(openState) => { // Use actions from store
        console.log(`Sheet onOpenChange called: openState=${openState}`);
        if (openState) {
          openChat();
          // ... (keep scroll logic) ...
          console.log("Sheet is opening, scheduling scrollToBottom(true)");
          setTimeout(() => {
            console.log("Executing delayed scrollToBottom(true)");
            scrollToBottom(true);
          }, 50); 
        } else {
          closeChat();
        }
      }}
    >
      {/* SheetTrigger */}
      <SheetTrigger asChild>
        <div className="fixed bottom-4 right-4 z-50">
          <Button
            onClick={toggleChat} // Use action from store
            variant="default" // Use default button variant
            size="icon"      // Make it icon sized
            className="rounded-full p-3 shadow-lg bg-[#8A6FE8] hover:bg-[#7F64D2] text-white" // Basic styling
            title="Open Magic AI Assistant"
            aria-label="Open Magic AI Assistant"
          >
            <span className="text-lg">✨</span>
          </Button>
        </div>
      </SheetTrigger>
      
      {/* SheetContent */}
      <SheetContent className="flex flex-col h-full w-full sm:max-w-lg p-0 bg-white">
        {/* Header */}
        <SheetHeader className="bg-[#8A6FE8] text-white h-[60px] flex flex-row justify-between items-center flex-shrink-0 px-4 border-b-0">
           {/* Sidebar Toggle Button */}
           <Button 
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-[#7F64D2] h-8 w-8 rounded-md"
            onClick={() => setSidebarVisible(!sidebarVisible)}
           >
             {sidebarVisible ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
           </Button>
           {/* Title */}
           <div className="flex items-center gap-2 flex-grow justify-center"> 
             <span className="text-xl">✨</span>
             <SheetTitle className="font-medium text-white">Magic AI Assistant</SheetTitle>
             <SheetDescription className={cn("sr-only")}>
               Chat with the Magic AI Assistant. Type your message below and press Send.
             </SheetDescription>
           </div>
           {/* Action Buttons Group */} 
           <div className="flex items-center gap-1">
             {/* Refresh Button (now triggers hard reload) */} 
             <Button 
               variant="ghost" 
               size="icon" 
               className="text-white hover:bg-[#7F64D2] h-8 w-8 rounded-md" 
               onClick={handleRefreshChat} // Uses updated handler
               aria-label="Hard Refresh Chat (Diagnostic)"
               title="Hard Refresh Chat (Diagnostic)"
             >
               <RotateCw className="h-5 w-5" aria-hidden="true" />
             </Button>

             {/* Close Button */} 
             <SheetClose asChild>
               <Button 
                 variant="ghost" 
                 size="icon" 
                 className="text-white hover:bg-[#7F64D2] h-8 w-8 rounded-md" 
                 aria-label="Close chat"
                 title="Close"
               >
                 <X className="h-5 w-5" aria-hidden="true" />
               </Button>
             </SheetClose>
           </div>
         </SheetHeader>

        {/* Main content with sidebar */}
        <div className="flex flex-1 overflow-hidden mt-0 pt-0">
          {/* Sidebar */}
          <div className={cn(
            "border-r border-gray-200 flex flex-col bg-gray-50 transition-all duration-300",
            sidebarVisible ? "w-[40%]" : "w-12"
          )}>
            {/* New Chat button */}
            <div className={cn(
              "border-b border-gray-200",
              sidebarVisible ? "p-3" : "" // Match session padding (p-3) when expanded
            )}>
              <Button 
                onClick={handleNewChat} // Uses Zustand action now
                className={cn(
                  "bg-[#8A6FE8] text-white hover:bg-[#7F64D2] w-full",
                  sidebarVisible ? "justify-start gap-2" : "h-10 justify-center items-center p-0" // Explicit height, centering, no padding for collapsed button
                )}
                title="New Chat"
              >
                <Plus className="h-5 w-5" />
                {sidebarVisible && <span>New Chat</span>}
              </Button>
            </div>
            
            {/* Sessions list */}
            <div className="flex-1 overflow-y-auto p-2">
              {isLoadingSessions ? (
                <div className={cn(
                  "flex items-center justify-center",
                  sidebarVisible ? "h-20" : "h-10"
                )}>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce mx-auto"></div>
                </div>
              ) : sessions.length === 0 ? (
                <div className={cn(
                  "flex items-center justify-center",
                  sidebarVisible ? "h-20" : "h-10"
                )}>
                  {sidebarVisible ? (
                    <p className="text-sm text-gray-500">No chat sessions yet</p>
                  ) : (
                    <span className="text-xs text-gray-500">0</span>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.slice(0, visibleSessionCount).map((session) => (
                    <button
                      key={session.id}
                      onClick={() => loadSessionById(session.id)} // Uses updated function
                      className={cn(
                         "rounded-md text-left focus:outline-none focus:ring-2 focus:ring-[#8A6FE8] focus:ring-offset-2 w-full",
                         sidebarVisible ? "p-3 hover:bg-gray-100" : "h-10 p-0 hover:bg-gray-100 flex justify-center items-center",
                         sessionId === session.id && sidebarVisible ? "bg-gray-100 font-semibold" : "", // Highlight active session
                         sessionId === session.id && !sidebarVisible ? "bg-gray-200" : "" // Different highlight for collapsed
                       )}
                      title={session.title}
                    >
                      <div className={cn(
                        "flex items-center",
                        sidebarVisible ? "gap-3" : "justify-center"
                      )}>
                        <MessageCircle className="h-5 w-5 text-[#8A6FE8] flex-shrink-0" />
                        {sidebarVisible && (
                          <div className="overflow-hidden">
                            <p className="text-sm font-medium text-gray-800">
                              {session.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(() => {
                                if (!session.created_at) return '';
                                const d = new Date(session.created_at);
                                const day = d.getDate().toString().padStart(2, '0');
                                const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
                                const hours = d.getHours().toString().padStart(2, '0');
                                const minutes = d.getMinutes().toString().padStart(2, '0');
                                return `${day}/${month} ${hours}:${minutes}`;
                              })()}
                            </p>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                  {/* Load More Sessions Button */}
                  {sessions.length > visibleSessionCount && (
                     <div className={cn(
                       "pt-2 flex justify-center",
                       !sidebarVisible && "p-0"
                     )}>
                       <Button
                         variant="outline"
                         className={cn(
                           "text-xs bg-white text-[#8A6FE8] border-[#8A6FE8] hover:bg-[#F5F1FF] w-full",
                           sidebarVisible ? "h-auto" : "h-10 p-0 flex justify-center items-center" // Match collapsed style
                         )}
                         onClick={() => setVisibleSessionCount(prev => prev + 10)}
                         title="Load More Sessions"
                       >
                         <ChevronDown className="h-5 w-5" />
                       </Button>
                     </div>
                  )}
                </div>
              )}
            </div>
          </div> {/* End Sidebar */}
          
          {/* Chat messages and input area */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Chat Messages Area */}
            <div 
              className="flex-1 p-4 overflow-y-auto flex flex-col gap-4 bg-white" 
              ref={chatMessagesRef}
            >
              {/* Load Previous Messages button - uses allMessages and derived displayedMessages length */}
              {allMessages.length > displayedMessages.length && ( 
                <div className="flex justify-center mb-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs bg-white text-[#8A6FE8] border-[#8A6FE8] hover:bg-[#F5F1FF]"
                    onClick={loadMoreMessages}
                  >
                    Load Previous Messages ({allMessages.length - displayedMessages.length} more)
                  </Button>
                </div>
              )}
              
              {/* Chat Stuck State - Manual Reload Button */}
              {chatStuckState === 'stuck' && (
                <div className="flex justify-center mb-4">
                  <Button
                    variant="outline"
                    className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200 flex items-center gap-2"
                    onClick={handleManualChatReload}
                  >
                    <RotateCw className="h-4 w-4" />
                    Chat Appears Stuck - Click to Reload
                  </Button>
                </div>
              )}
              
              {/* Messages map - uses the derived displayedMessages */}
              {displayedMessages.map((message, index) => {
                 const actualIndex = allMessages.length - displayedMessages.length + index;
                 const isEditingThisMessage = editingMessageIndex === actualIndex;
                
                 // Log audioUrl (keep)
                 if (message.audioUrl) {
                   console.log(`[Render] Rendering message index ${index} (actual: ${actualIndex}) with audioUrl:`, message.audioUrl);
                 }

                 // Render edit input or message bubble based on isEditingThisMessage
                 if (isEditingThisMessage) {
                    return (
                      <div key={`edit-${actualIndex}`} className="flex flex-col items-end w-full gap-1 my-1">
                        <textarea
                           value={editingContent}
                           onChange={(e) => setEditingContent(e.target.value)}
                           className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#8A6FE8]"
                           rows={3}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
                          <Button size="sm" className="bg-[#8A6FE8] hover:bg-[#7F64D2]" onClick={() => handleSaveEdit(actualIndex)}>Save</Button>
                        </div>
                      </div>
                    );
                 }

                 // Otherwise, render the normal message bubble
                 return (
                  <div 
                    key={actualIndex} // Use actualIndex for key
                    className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}
                  >
                    <div className={`group flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} w-full`}>
                      <div 
                        className={`rounded-lg px-4 py-2 max-w-[85%] text-sm ${ 
                          message.role === 'user' 
                            ? 'bg-[#8A6FE8] text-white' 
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {message.role === 'assistant' && <span className="mr-1">🧙‍♂️</span>}
                         
                        {/* Conditionally render Audio Player or Text */} 
                        {message.audioUrl ? (
                          <div className="flex flex-col items-start gap-2 py-1"> 
                             {message.content && message.content.trim() && (
                               <div
                                 className="message-content text-white" 
                                 dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }}
                               />
                             )}
                             <audio controls src={message.audioUrl} className="max-w-full h-10 rounded"> 
                                Your browser does not support the audio element.
                             </audio>
                          </div>
                        ) : (
                          /* Standard Text Message Content */
                          <div
                            className="message-content"
                            dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }}
                          />
                        )}
                        
                        {message.role === 'user' && !message.audioUrl && <span className="ml-1">👤</span>} {/* Hide user icon for audio */} 
                      </div>
                    </div>
                     {/* Action Buttons & Timestamp */} 
                    <div className={`flex items-center gap-1 mt-1 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                       {/* Timestamp */}
                       {message.timestamp && (() => {
                          const timestampStr = message.timestamp;
                          const d = new Date(timestampStr);
                          const day = d.getDate().toString().padStart(2, '0');
                          const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
                          const hours = d.getHours().toString().padStart(2, '0');
                          const minutes = d.getMinutes().toString().padStart(2, '0');
                          return (
                            <span className="text-xs text-gray-400 mr-2">
                              {`${day}/${month} ${hours}:${minutes}`}
                            </span>
                          );
                       })()}
                       {/* Read Aloud / Stop Button */}
                       <Button
                         variant="ghost"
                         size="icon"
                         className={cn(
                            "h-6 w-6 text-gray-500 hover:text-gray-700",
                            speakingMessageIndex === actualIndex && "text-[#8A6FE8]" // Highlight when speaking
                         )}
                         onClick={() => handleReadAloud(message.content, actualIndex)} 
                         title={speakingMessageIndex === actualIndex ? "Stop reading" : "Read aloud"}
                       >
                         <Volume2 className="h-4 w-4" />
                       </Button>
                       {/* Edit Button - only for user messages */}
                       {message.role === 'user' && ( 
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-6 w-6 text-gray-500 hover:text-gray-700"
                           onClick={() => handleStartEdit(index)} // Pass displayed index to find actualIndex
                           title="Edit message"
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                       )}
                       {/* Copy Button */}
                       <Button
                         variant="ghost"
                         size="icon"
                         className="h-6 w-6 text-gray-500 hover:text-gray-700"
                         onClick={() => navigator.clipboard?.writeText(message.content).then(() => console.log('Copied')).catch(err => console.error('Copy failed', err))}
                       >
                         <Copy className="h-4 w-4" />
                       </Button>
                    </div>
                  </div>
                )
              })}
              
              {/* Global styles (keep) */}
              <style jsx global>{`
                .message-content p {
                  margin-bottom: 12px;
                }
                .message-content p:last-child {
                  margin-bottom: 0;
                }
                .message-content strong {
                  font-weight: 600;
                  display: block;
                  margin-top: 16px;
                  margin-bottom: 8px;
                }
                .message-content a {
                  color: #8A6FE8;
                  text-decoration: none;
                }
                .message-content a:hover {
                  text-decoration: underline;
                }
                .message-content br {
                  display: block;
                  margin: 4px 0;
                }
                .message-content ul {
                  margin: 8px 0;
                  padding-left: 16px;
                }
                .message-content li {
                  margin-bottom: 4px;
                  position: relative;
                }
                .message-content {
                  word-break: break-word;
                }
              `}</style>
              
              {/* Loading indicator (keep) */}
              {isLoading && (
                 <div className="flex justify-start">
                   <div className="bg-gray-100 text-gray-800 rounded-lg px-4 py-2 flex items-center gap-2 text-sm">
                     <span className="text-base mr-1">🧙‍♂️</span>
                     <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                     <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                     <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                   </div>
                 </div>
              )}
            </div> {/* End Chat Messages Area */}
            
            {/* Input Area */}
            <SheetFooter className="border-t p-3 bg-white flex-shrink-0"> 
              <div className="flex items-center gap-2 w-full">
                {/* Mic/Trash Button */} 
                {mediaRecorderAvailable && (
                   <Button 
                    variant="ghost"
                    size="icon" 
                    onClick={
                      isRecording ? stopRecording : 
                      audioBlob ? cancelRecording : startRecording_modified
                    }
                    disabled={!mediaRecorderAvailable} 
                    className={cn(
                      "text-gray-500 hover:text-[#8A6FE8] disabled:opacity-50",
                      isRecording && "text-red-500 animate-pulse", // Red pulsing when recording
                      audioBlob && !isRecording && "text-red-600 hover:text-red-800" // Red when ready to discard
                    )}
                    title={isRecording ? "Stop Recording" : audioBlob ? "Discard Recording" : "Start Recording"}
                  >
                    {isRecording ? <Square className="h-5 w-5" /> : 
                     audioBlob ? <Trash2 className="h-5 w-5" /> : 
                     <Mic className="h-5 w-5" />
                    }
                  </Button>
                )}
                {/* Text Input */} 
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={audioBlob ? "Audio recorded. Add optional text..." : "Ask the Magic Assistant... ✨"}
                  disabled={isLoading || isRecording}
                  className={cn(
                    "rounded-md border border-gray-300 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#8A6FE8]",
                    !audioBlob && "flex-1" // Only grow when no audio blob exists
                  )}
                />
                {/* Audio Player & Send Button Area */} 
                {audioBlob && !isRecording && (
                  <>
                    {/* Audio Preview Player */} 
                    {audioInputUrl && (
                      <audio 
                        controls 
                        src={audioInputUrl} 
                        className="h-8 rounded"
                        style={{ height: '32px' }} // Explicit height to match button size
                      >
                        Your browser does not support the audio element.
                      </audio>
                    )}
                    {/* Send Button */}
                    <Button 
                      onClick={handleSendMessage}
                      disabled={isLoading} 
                      className="bg-[#8A6FE8] hover:bg-[#7F64D2] text-white h-10 w-10 p-0 flex items-center justify-center"
                      title="Send Recording"
                    >
                      <Send className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </>
                )}
                {/* Standard Send Button (no audio) */} 
                {!audioBlob && !isRecording && (
                  <Button 
                    onClick={handleSendMessage}
                    disabled={isLoading || !inputValue.trim()} 
                    className="bg-[#8A6FE8] hover:bg-[#7F64D2] text-white h-10 w-10 p-0 flex items-center justify-center"
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </SheetFooter>
          </div> {/* End Chat/Input container */}
        </div> {/* End Main content */}
      </SheetContent>
    </Sheet>
  );
}

// Export as dynamic component to prevent SSR issues
export default dynamic(() => Promise.resolve(ChatButton), {
  ssr: false
});